<template>
  <div>
    <svg
      class="nuxt-rating-star-svg"
      :height="starSize"
      :width="starSize"
      :viewBox="viewBox"
      @mousemove="mouseMoving"
      @click="selected"
      @touchstart="touchStart"
      @touchend="touchEnd"
    >
      <linearGradient :id="grad" x1="0" x2="100%" y1="0" y2="0">
        <stop
          :offset="starFill"
          :stop-color="getColor(activeColor)"
          :stop-opacity="getOpacity(activeColor)"
        />
        <stop
          :offset="starFill"
          :stop-color="getColor(inactiveColor)"
          :stop-opacity="getOpacity(inactiveColor)"
        />
      </linearGradient>

      <polygon
        :height="starSize"
        :width="starSize"
        :points="starPointsToString"
        :fill="gradId"
        :stroke="getBorderColor"
        :stroke-width="borderWidth"
        :stroke-linejoin="strokeLinejoin"
      />
      <polygon :points="starPointsToString" :fill="gradId" />
    </svg>
  </div>
</template>

<script lang="ts" setup>
// nuxt-rating v0.1.6 (MIT License, https://github.com/TonyFresneau/nuxt-rating) の
// dist/runtime/components/NuxtStar.vue を vendor 取り込みしたもの。
// upstream が noUncheckedIndexedAccess 非対応かつメンテナンスが停滞しているため自前管理する。
// 取り込みにあたり、型定義(NuxtStarProps)と AlphaColor ヘルパーをこのファイルへ内包し、
// noUncheckedIndexedAccess でエラーになる箇所を修正している。
// また、upstream は gradient id を activeColor で初期化し onMounted で Math.random に
// 差し替えるため、SSR HTML 内で全星の id が重複していた（HTML 仕様違反）。
// SSR/CSR で一意かつ安定な id を生成する useId に変更している。
//
// MIT の許諾表示を配信物にも残すため /*! 形式で記述する(通常の // コメントは除去される)。
// SSR バンドルには残るが、クライアント側は Vite 8 の transform が terser に渡る前に
// 除去するため残らない。バンドル全体の第三者ライセンス表記は別課題として扱う。
/*!
 * nuxt-rating v0.1.6 - Copyright (c) 2023 Aurion SARL - MIT License
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions: The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import { computed, ref, useId } from 'vue'

interface NuxtStarProps {
  fill: number
  points: number[]
  size: number | string
  starId: number | string
  activeColor: string
  inactiveColor: string
  borderColor: string
  skeletonColor?: string
  borderWidth: number
  roundedCorners: boolean
}

// upstream: dist/runtime/helpers/AlphaColor.mjs
class AlphaColor {
  color: string
  opacity: number

  constructor(color: string) {
    this.color = color
    this.opacity = 1
  }

  parseAlphaColor() {
    const match = this.color.match(/rgba?\(([^)]+)\)/)
    if (match && match[1] !== undefined) {
      const [r, g, b, a] = match[1].split(',').map(Number)
      this.color = `rgb(${r}, ${g}, ${b})`
      this.opacity = a !== undefined ? a : 1
    }
    return this
  }
}

const props = defineProps<NuxtStarProps>()

const emit = defineEmits(['star-mouse-move', 'star-selected'])

const getColor = (color: string) =>
  new AlphaColor(color).parseAlphaColor().color
const getOpacity = (color: string) =>
  new AlphaColor(color).parseAlphaColor().opacity

const grad = useId()
const isStarActive = ref(true)

const parseSize = (size: string | number): number => {
  if (typeof size === 'number') return size
  const value = parseFloat(size)
  return isNaN(value) ? 0 : value
}

const size = parseSize(props.size)

const starPointsToString = computed(() => props.points.join(','))
const gradId = computed(() => `url(#${grad})`)
const starSize = computed(() => {
  return (
    size +
    (props.roundedCorners && props.borderWidth <= 0 ? 6 : props.borderWidth)
  )
})
const starFill = computed(() => `${props.fill}%`)
const getBorderColor = computed(() =>
  props.roundedCorners && props.borderWidth <= 0
    ? props.inactiveColor
    : props.borderColor
)
const maxSize = computed(() => {
  const max = Math.max(...props.points)
  return isFinite(max) ? max : 100
})
const viewBox = computed(() => `0 0 ${maxSize.value} ${maxSize.value}`)
const strokeLinejoin = computed(() =>
  props.roundedCorners ? 'round' : 'miter'
)

const mouseMoving = (event: MouseEvent) => {
  emit('star-mouse-move', {
    event,
    position: getPosition(event),
    id: props.starId,
  })
}

const touchStart = () => {
  isStarActive.value = true
}

const touchEnd = () => {
  isStarActive.value = false
}

const getPosition = (event: MouseEvent) => {
  const starWidth = (92 / 100) * size
  const offset = Math.max(event.offsetX, 1)
  return Math.min(Math.round((100 / starWidth) * offset), 100)
}

const selected = (event: MouseEvent) => {
  emit('star-selected', {
    id: props.starId,
    position: getPosition(event),
  })
}
</script>

<style scoped>
.nuxt-rating-star-svg {
  overflow: visible !important;
}
</style>
